/**
 * RemoteAssetBlobStore — a BlobStore view of a site's CLOUD asset bucket,
 * spoken over the BFF's existing same-origin routes:
 *   - put   → POST /api/sites/:id/assets  (the rich, sha-verified upload)
 *   - get   → GET  /api/v1/assets/:id/:sha (the public content-addressed route)
 *   - has   → the local "synced" manifest (a cache of what's already uploaded)
 *
 * This lets the publish-sync be `syncBlobStores(opfsBucket, remoteAssetStore)`
 * — the same content-addressed copy primitive the in-browser S3 facade uses.
 *
 * `has`/`get` talk to the server's `/_s3/<siteId>/<sha>` facade (the same URL
 * shape the in-browser Service Worker answers locally) — `has` short-circuits
 * on the local synced manifest first, so the network HEAD only fires for assets
 * this device hasn't already uploaded. Writes still go through the rich upload
 * endpoint (sha-verified + DB row), so `put` POSTs to /api/sites/:id/assets.
 */
import { ASSET_PREFIX, type BlobStore } from "@website-builder/blob-store";

export interface RemoteAssetBlobStoreOptions {
  /** Cloud site id the assets belong to (the POST/GET target). */
  readonly siteId: string;
  /** Content-Type to send per key (the OPFS blob carries no mime). */
  readonly contentTypeForKey?: (key: string) => string | undefined;
  /** Shas already known to be on the backend (the synced manifest). */
  readonly synced: ReadonlySet<string>;
}

function shaOf(key: string): string {
  return key.startsWith(ASSET_PREFIX) ? key.slice(ASSET_PREFIX.length) : key;
}

export class RemoteAssetBlobStore implements BlobStore {
  constructor(private readonly opts: RemoteAssetBlobStoreOptions) {}

  async has(key: string): Promise<boolean> {
    const sha = shaOf(key);
    // Fast path: the local manifest records what this device already uploaded,
    // so we skip the network for re-publishes. On a miss, HEAD the cloud facade
    // (cross-device-correct: another device may have uploaded the same bytes).
    if (this.opts.synced.has(sha)) return true;
    const r = await fetch(`/_s3/${this.opts.siteId}/${sha}`, {
      method: "HEAD",
      credentials: "same-origin",
    });
    return r.ok;
  }

  async put(key: string, data: Uint8Array | Blob): Promise<void> {
    const sha = shaOf(key);
    const contentType =
      this.opts.contentTypeForKey?.(key) ??
      (data instanceof Blob && data.type
        ? data.type
        : "application/octet-stream");
    // Normalize to a Blob for the request body (a bare Uint8Array isn't a
    // BodyInit under the strict DOM lib). In practice `data` is already the
    // OPFS Blob handed over by syncBlobStores.
    const body =
      data instanceof Blob ? data : new Blob([data as unknown as ArrayBuffer]);
    const r = await fetch(`/api/sites/${this.opts.siteId}/assets`, {
      method: "POST",
      headers: {
        "Content-Type": contentType,
        // Content address — lets the server stream straight to its object
        // store and verify the bytes without buffering to hash.
        "X-Asset-Sha256": sha,
      },
      body,
      credentials: "same-origin",
    });
    if (!r.ok) throw new Error(`upload failed (HTTP ${r.status})`);
  }

  async getBlob(key: string): Promise<Blob | null> {
    const r = await fetch(`/_s3/${this.opts.siteId}/${shaOf(key)}`, {
      credentials: "same-origin",
    });
    return r.ok ? await r.blob() : null;
  }

  async get(key: string): Promise<Uint8Array | null> {
    const blob = await this.getBlob(key);
    return blob ? new Uint8Array(await blob.arrayBuffer()) : null;
  }

  /** Best-effort: the keys we know are uploaded (the manifest). */
  async list(): Promise<string[]> {
    return [...this.opts.synced].map((sha) => `${ASSET_PREFIX}${sha}`);
  }

  async delete(): Promise<void> {
    throw new Error("RemoteAssetBlobStore.delete is not supported");
  }
}
